import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { Shield, TrendingUp, AlertTriangle, ChevronRight, Award } from "lucide-react";

const TONE_STYLE = {
    success: { bar: "bg-emerald-500", tag: "text-emerald-700 bg-emerald-50 border-emerald-200", label: "On track / Waived" },
    warning: { bar: "bg-amber-500",  tag: "text-amber-700 bg-amber-50 border-amber-200",       label: "Behind" },
    danger:  { bar: "bg-red-500",     tag: "text-red-700 bg-red-50 border-red-200",             label: "At risk" },
    neutral: { bar: "bg-slate-400",  tag: "text-slate-700 bg-slate-100 border-slate-200",     label: "Setup needed" },
};

export default function AnnualFeeWaiver() {
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get("/annual-fee-waiver").then((r) => setData(r.data)).catch(() => setData({ summary: {}, rows: [] }));
    }, []);

    if (!data) return <div className="text-stone-500">Loading…</div>;

    return (
        <div data-testid="annual-fee-waiver-page">
            <div className="mb-6">
                <div className="eyebrow flex items-center gap-1.5"><Award size={11} /> Annual Fee Waiver Tracker</div>
                <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                    Every card, every fee, on track.
                </h1>
                <p className="text-sm text-stone-500 mt-3 max-w-2xl">
                    Live waiver progress for every active card — spend YTD vs criteria, days to renewal,
                    and a recommendation for each: keep, push spend, downgrade, or close.
                </p>
            </div>

            {/* Summary strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="kpi-card">
                    <div className="eyebrow">Cards tracked</div>
                    <div className="font-display text-2xl mt-2 tabular-nums">{data.summary?.cards_count ?? 0}</div>
                </div>
                <div className="kpi-card">
                    <div className="eyebrow">Total annual fees</div>
                    <div className="font-display text-2xl mt-2 tabular-nums">{fmtMoney(data.summary?.total_fees_annualized || 0)}</div>
                </div>
                <div className="kpi-card">
                    <div className="eyebrow flex items-center gap-1.5"><TrendingUp size={11} /> On track / Waived</div>
                    <div className="font-display text-2xl mt-2 tabular-nums text-emerald-700">{data.summary?.on_track_or_waived || 0}</div>
                </div>
                <div className="kpi-card">
                    <div className="eyebrow flex items-center gap-1.5"><AlertTriangle size={11} /> At risk / Behind</div>
                    <div className="font-display text-2xl mt-2 tabular-nums text-red-700">{data.summary?.at_risk_or_behind || 0}</div>
                </div>
            </div>

            {data.summary?.no_criteria_set > 0 && (
                <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex items-center gap-2">
                    <AlertTriangle size={13} />
                    {data.summary.no_criteria_set} card(s) charge an annual fee but have no waiver criteria set — add the criteria on the card form to enable tracking.
                </div>
            )}

            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <table className="dense-table w-full">
                    <thead>
                        <tr>
                            <th>Card</th>
                            <th className="text-right">Annual fee</th>
                            <th>Waiver criteria</th>
                            <th className="text-right">Spend YTD</th>
                            <th className="text-right">Shortfall</th>
                            <th className="w-32">Progress</th>
                            <th>Renewal</th>
                            <th>Verdict</th>
                            <th>Recommendation</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(data.rows || []).length === 0 && (
                            <tr><td colSpan={9} className="text-center text-stone-500 py-10">No active cards yet.</td></tr>
                        )}
                        {(data.rows || []).map((r) => {
                            const style = TONE_STYLE[r.tone] || TONE_STYLE.neutral;
                            return (
                                <tr key={r.card_id} data-testid={`afw-row-${r.card_id}`}>
                                    <td>
                                        <Link to={`/cards/${r.card_id}/view`} className="text-stone-900 hover:underline font-medium">{r.bank_name}</Link>
                                        <div className="text-xs text-stone-500">{r.card_name} · ••{r.last_4}</div>
                                    </td>
                                    <td className="text-right tabular-nums font-medium">{r.annual_fee ? fmtMoney(r.annual_fee) : <span className="text-stone-400">Nil</span>}</td>
                                    <td className="text-xs text-stone-600">
                                        {r.waiver_criteria || <span className="text-stone-400">—</span>}
                                        {r.waiver_threshold && (
                                            <div className="text-[11px] text-stone-500 tabular-nums">Threshold: {fmtMoney(r.waiver_threshold)}</div>
                                        )}
                                    </td>
                                    <td className="text-right tabular-nums">{fmtMoney(r.spend_ytd)}</td>
                                    <td className="text-right tabular-nums">
                                        {r.shortfall === null ? <span className="text-stone-400">—</span> :
                                         r.shortfall === 0 ? <span className="text-emerald-700">Cleared</span> :
                                         <span className="text-red-700">{fmtMoney(r.shortfall)}</span>}
                                    </td>
                                    <td>
                                        {r.progress_pct === null ? <span className="text-stone-400 text-xs">—</span> : (
                                            <div className="w-full">
                                                <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                                    <div className={`h-full ${style.bar}`} style={{ width: `${Math.min(100, r.progress_pct)}%` }} />
                                                </div>
                                                <div className="text-[10.5px] text-stone-500 mt-0.5 tabular-nums">{r.progress_pct}%</div>
                                            </div>
                                        )}
                                    </td>
                                    <td className="text-xs">
                                        {r.renewal_month ? (
                                            <>
                                                <div className="tabular-nums">Month {r.renewal_month}</div>
                                                {r.days_to_renewal !== null && (
                                                    <div className={`text-[10.5px] tabular-nums ${r.days_to_renewal < 45 ? "text-red-700" : "text-stone-500"}`}>{r.days_to_renewal}d</div>
                                                )}
                                            </>
                                        ) : <span className="text-stone-400">—</span>}
                                    </td>
                                    <td>
                                        <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${style.tag}`}>{r.verdict}</span>
                                    </td>
                                    <td className="text-xs text-stone-700">
                                        <div className="flex items-center gap-1">
                                            {r.recommendation}
                                            <Link to={`/cards/${r.card_id}/edit`} className="text-blue-700 hover:underline shrink-0" title="Edit card">
                                                <ChevronRight size={12} />
                                            </Link>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
